import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Search,
  X,
} from "lucide-react";
import * as XLSX from "xlsx";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  getReportMetricRecords,
  ReportApproachRecord,
  ReportApplicationRecord,
  ReportListableMetric,
  ReportMetricRecordsParams,
  ReportMetricRecordsResponse,
} from "../../services/reportApi";
import { toast } from "sonner";

const METRIC_LABELS: Record<ReportListableMetric, string> = {
  approaches: "Total Approaches",
  applications: "Total Applications",
  scheduled: "Total Scheduled",
  attended: "Total Attended",
  interviewNotAttended: "Interview Not Attended",
  selections: "Total Selections",
  joins: "Total Joins",
  notJoined: "Not Joined",
  verified: "Total Verified",
  notVerified: "Total Not Verified",
  pending: "Total Pending",
  approved: "Approved",
  notApproved: "Not Approved",
  approvalPending: "Approval Pending",
  tenureClaimed: "Tenure Claimed",
};

const LISTABLE_METRICS = new Set<string>(Object.keys(METRIC_LABELS));

const EXPORT_BATCH_SIZE = 5000;
const MAX_EXPORT_ROWS = 200000;

const formatDisplayDate = (value?: string) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatExportDate = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return format(date, "yyyy-MM-dd");
};

const ReportMetricDetailPage = () => {
  const navigate = useNavigate();
  const { metricKey } = useParams<{ metricKey: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState(() => searchParams.get("dateFrom") || "");
  const [dateTo, setDateTo] = useState(() => searchParams.get("dateTo") || "");
  const [data, setData] = useState<ReportMetricRecordsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const recruiterId = searchParams.get("recruiterId") || undefined;
  const role = searchParams.get("role") || undefined;

  const isValidDateRange = useMemo(() => {
    if (!dateFrom || !dateTo) return true;
    return dateFrom <= dateTo;
  }, [dateFrom, dateTo]);

  const filterParams = useMemo<ReportMetricRecordsParams>(
    () => ({
      recruiterId,
      role,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [recruiterId, role, dateFrom, dateTo]
  );

  const syncDateParams = useCallback(
    (from: string, to: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (from) next.set("dateFrom", from);
          else next.delete("dateFrom");
          if (to) next.set("dateTo", to);
          else next.delete("dateTo");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleDateFromChange = (value: string) => {
    setDateFrom(value);
    setPage(1);
    syncDateParams(value, dateTo);
  };

  const handleDateToChange = (value: string) => {
    setDateTo(value);
    setPage(1);
    syncDateParams(dateFrom, value);
  };

  const clearDateFilters = () => {
    setDateFrom("");
    setDateTo("");
    setPage(1);
    syncDateParams("", "");
  };

  const backUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (role) params.set("role", role);
    if (recruiterId) params.set("recruiterId", recruiterId);
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    const query = params.toString();
    return query ? `/report?${query}` : "/report";
  }, [role, recruiterId, dateFrom, dateTo]);

  const metricLabel =
    metricKey && LISTABLE_METRICS.has(metricKey)
      ? METRIC_LABELS[metricKey as ReportListableMetric]
      : "Report detail";

  const fetchRecords = useCallback(async () => {
    if (!metricKey || !LISTABLE_METRICS.has(metricKey) || !isValidDateRange) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await getReportMetricRecords(metricKey as ReportListableMetric, {
        ...filterParams,
        page,
        limit: 20,
        search: searchTerm || undefined,
      });
      setData(response?.data ?? null);
    } catch (error) {
      console.error("Failed to fetch report metric records:", error);
      setData(null);
      toast.error("Failed to load records");
    } finally {
      setIsLoading(false);
    }
  }, [metricKey, filterParams, page, searchTerm, isValidDateRange]);

  const handleExport = async () => {
    if (!metricKey || !LISTABLE_METRICS.has(metricKey) || !isValidDateRange) return;

    setIsExporting(true);
    toast.loading("Preparing export...", { id: "report-metric-export" });

    try {
      let currentPage = 1;
      let totalResults = 0;
      let totalPages = 1;
      const allRecords: (ReportApproachRecord | ReportApplicationRecord)[] = [];

      while (currentPage <= totalPages && allRecords.length < MAX_EXPORT_ROWS) {
        const remaining = MAX_EXPORT_ROWS - allRecords.length;
        const response = await getReportMetricRecords(metricKey as ReportListableMetric, {
          ...filterParams,
          page: currentPage,
          limit: Math.min(EXPORT_BATCH_SIZE, remaining),
          search: searchTerm || undefined,
          export: true,
        });

        const batch = response?.data;
        if (!batch) break;

        totalResults = batch.totalResults;
        totalPages = batch.totalPages;
        allRecords.push(...(batch.records ?? []));

        const progressLabel =
          totalResults > 0
            ? `Exporting ${allRecords.length.toLocaleString("en-IN")} of ${totalResults.toLocaleString("en-IN")} rows...`
            : `Exporting ${allRecords.length.toLocaleString("en-IN")} rows...`;
        toast.loading(progressLabel, { id: "report-metric-export" });

        if ((batch.records ?? []).length === 0) break;
        currentPage += 1;
      }

      if (allRecords.length === 0) {
        toast.error("No data to export", { id: "report-metric-export" });
        return;
      }

      const isApproachesExport = data?.source === "approaches" || metricKey === "approaches";

      const excelData = isApproachesExport
        ? (allRecords as ReportApproachRecord[]).map((row) => ({
            Name: row.name || "",
            Phone: row.phone || "",
            Email: row.email || "",
            City: row.city || "",
            Source: row.source || "",
            "Call Status": row.callStatus || "",
            "Sourced By": row.sourcedBy || "",
            Led: row.led || "",
            SHM: row.shm || "",
            Created: formatExportDate(row.createdAt),
          }))
        : (allRecords as ReportApplicationRecord[]).map((row) => ({
            Candidate: row.candidateName || "",
            Phone: row.candidatePhone || "",
            Email: row.candidateEmail || "",
            Job: row.jobTitle || "",
            Company: row.companyName || "",
            Location: row.location || "",
            Status: row.currentStatus || "",
            "Sourced By": row.sourcedBy || "",
            Led: row.led || "",
            SHM: row.shm || "",
            Applied: formatExportDate(row.appliedOn),
            Selected: formatExportDate(row.selectedDate),
            Joined: formatExportDate(row.joiningDate),
            "Last Working Day": formatExportDate(row.lastWorkingDay),
            Verification: row.verificationStatus || "",
          }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, metricLabel.slice(0, 31));

      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${metricKey}_${format(new Date(), "yyyy-MM-dd")}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(
        allRecords.length < totalResults
          ? `Exported ${allRecords.length.toLocaleString("en-IN")} of ${totalResults.toLocaleString("en-IN")} rows (limit reached)`
          : "Excel file downloaded successfully",
        { id: "report-metric-export" }
      );
    } catch (error) {
      console.error("Failed to export report metric records:", error);
      toast.error("Failed to export data", { id: "report-metric-export" });
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPage(1);
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  if (!metricKey || !LISTABLE_METRICS.has(metricKey)) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6">
        <p className="text-sm text-gray-500">Invalid report metric.</p>
        <Button type="button" variant="outline" onClick={() => navigate("/report")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to report
        </Button>
      </div>
    );
  }

  const isApproaches = data?.source === "approaches";
  const approachRecords = (data?.records ?? []) as ReportApproachRecord[];
  const applicationRecords = (data?.records ?? []) as ReportApplicationRecord[];
  const totalPages = data?.totalPages ?? 0;
  const totalResults = data?.totalResults ?? 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
        <div className="flex flex-col gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => navigate(backUrl)}
              className="mt-0.5 shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0 flex-1">
              <h1 className="truncate text-lg font-semibold text-gray-900 sm:text-xl">
                {metricLabel}
              </h1>
              <p className="mt-1 text-xs text-gray-500 sm:text-sm">
                {isLoading ? "Loading..." : `${totalResults.toLocaleString("en-IN")} record${totalResults === 1 ? "" : "s"}`}
                {(dateFrom || dateTo) && (
                  <>
                    {" "}
                    · {dateFrom && dateTo ? `${dateFrom} to ${dateTo}` : dateFrom || dateTo}
                  </>
                )}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={handleExport}
              disabled={isLoading || isExporting || !isValidDateRange || totalResults === 0}
              className="h-10 shrink-0 rounded-lg border-gray-200"
            >
              {isExporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Download
            </Button>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search name, phone, email..."
                className="h-10 rounded-lg border-gray-200 pl-9 focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => handleDateFromChange(e.target.value)}
              max={dateTo || undefined}
              className="h-10 w-full rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 sm:w-[148px]"
              title="From Date"
              placeholder="From Date"
            />

            <Input
              type="date"
              value={dateTo}
              onChange={(e) => handleDateToChange(e.target.value)}
              min={dateFrom || undefined}
              className="h-10 w-full rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 sm:w-[148px]"
              title="To Date"
              placeholder="To Date"
            />

            {(dateFrom || dateTo) && (
              <Button
                type="button"
                variant="outline"
                onClick={clearDateFilters}
                className="h-10 rounded-lg border-gray-200 hover:bg-orange-50"
              >
                <X className="mr-2 h-4 w-4" />
                Clear dates
              </Button>
            )}
          </div>

          {!isValidDateRange && (
            <p className="text-sm text-red-600">From date must be on or before To date.</p>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto bg-[#EDEEF1]/40 p-4 sm:p-6">
        <Card className="border border-gray-200 bg-white shadow-sm">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex h-64 flex-col items-center justify-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-[#EC6E51]" />
                <p className="text-sm text-gray-500">Loading records...</p>
              </div>
            ) : !isValidDateRange ? (
              <div className="flex h-64 items-center justify-center">
                <p className="text-sm text-gray-500">Fix the date range to view records.</p>
              </div>
            ) : totalResults === 0 ? (
              <div className="flex h-64 items-center justify-center">
                <p className="text-sm text-gray-500">No records found for this metric.</p>
              </div>
            ) : isApproaches ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[960px] text-left text-sm">
                  <thead className="border-b border-gray-200 bg-gray-50/80 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Phone</th>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">City</th>
                      <th className="px-4 py-3">Source</th>
                      <th className="px-4 py-3">Call status</th>
                      <th className="px-4 py-3">Sourced by</th>
                      <th className="px-4 py-3">Led</th>
                      <th className="px-4 py-3">SHM</th>
                      <th className="px-4 py-3">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {approachRecords.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50/80">
                        <td className="px-4 py-3 font-medium text-gray-900">{row.name || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.phone || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.email || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.city || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.source || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.callStatus || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.sourcedBy || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.led || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.shm || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{formatDisplayDate(row.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1480px] text-left text-sm">
                  <thead className="border-b border-gray-200 bg-gray-50/80 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3">Candidate</th>
                      <th className="px-4 py-3">Phone</th>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Job</th>
                      <th className="px-4 py-3">Company</th>
                      <th className="px-4 py-3">Location</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Sourced by</th>
                      <th className="px-4 py-3">Led</th>
                      <th className="px-4 py-3">SHM</th>
                      <th className="px-4 py-3">Applied</th>
                      <th className="px-4 py-3">Selected</th>
                      <th className="px-4 py-3">Joined</th>
                      <th className="px-4 py-3">Last working day</th>
                      <th className="px-4 py-3">Verification</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {applicationRecords.map((row) => (
                      <tr key={row.applicationId} className="hover:bg-gray-50/80">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {row.candidateName || "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{row.candidatePhone || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.candidateEmail || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.jobTitle || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.companyName || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.location || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.currentStatus || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.sourcedBy || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.led || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.shm || "—"}</td>
                        <td className="px-4 py-3 text-gray-700">{formatDisplayDate(row.appliedOn)}</td>
                        <td className="px-4 py-3 text-gray-700">{formatDisplayDate(row.selectedDate)}</td>
                        <td className="px-4 py-3 text-gray-700">{formatDisplayDate(row.joiningDate)}</td>
                        <td className="px-4 py-3 text-gray-700">{formatDisplayDate(row.lastWorkingDay)}</td>
                        <td className="px-4 py-3 text-gray-700">{row.verificationStatus || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between gap-3">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1 || isLoading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= totalPages || isLoading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportMetricDetailPage;
